import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { convertV3CalendarEventToV2Appointment } from '../views/CalendarV3/helpers';
import { useGetCalendarV3QueriesData } from './scheduler-v3';
import { useAppointmentsInfoShallowStore } from './use-appointments-info-store';

/**
 * Legacy hook for appointment info (Schedule V2)
 */
const useGetAppointmentInfoLegacy = (eventId: string) => {
  const { appointments } = useAppointmentsInfoShallowStore('appointments');
  const appointmentDetails = appointments?.find((appointment) => appointment.id === eventId);

  return {
    appointmentDetails: appointmentDetails,
  };
};

/**
 * Enhanced V3 hook that safely handles missing context by catching errors
 * when the context is not available and the base hook throws an error
 */
const useGetAppointmentInfoV3Safe = (eventId: string) => {
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');
  try {
    // Try to call the hook but handle potential context errors
    const calendarV3Data = useGetCalendarV3QueriesData();

    // If context is unavailable, calendarV3Data will be undefined or have empty data
    const calendarEvents = calendarV3Data?.calendarEventsData?.events?.find((e) => e.id === eventId);

    return {
      appointmentDetails: calendarEvents
        ? convertV3CalendarEventToV2Appointment(calendarEvents, locationTimezoneMap)
        : null,
      isV3DataAvailable: !!calendarV3Data?.calendarEventsData,
      hasError: false,
    };
  } catch (error) {
    // Base hook threw an error (likely missing context)
    return {
      appointmentDetails: null,
      isV3DataAvailable: false,
      hasError: true,
    };
  }
};

export const useGetAppointmentInfo = (eventId: string) => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

  // Always call both hooks to follow Rules of Hooks
  const legacyResult = useGetAppointmentInfoLegacy(eventId);
  const v3Result = useGetAppointmentInfoV3Safe(eventId);

  // Use V3 result if flag is enabled, V3 data is available, and no errors occurred
  if (isScheduleV3FlagEnabled && v3Result.isV3DataAvailable && !v3Result.hasError) {
    return {
      appointmentDetails: v3Result.appointmentDetails,
    };
  }

  // Fallback to legacy result if V3 is disabled, unavailable, or errored
  return legacyResult;
};
